import * as React from 'react';
import {
  ListingWithReps,
  OfferSettingsJsonType,
  ListingOfferNotificationsDTO,
  PendingRegisteredOffersListing,
} from '@bb/domain/dist/types';
import Axios from 'axios';
import Loading from '@app/react-common/Loading';
import { IRootScopeService } from 'angular';
import Empty from '@app/react-common/Empty';
import { AuthContextUser } from 'contexts';
import { OfferDoc } from '@bb/domain/dist/schemas';
import type { JsonObject } from '@/app-package';
import OfferListTable from './OfferListTable';
import Form801Modal, {
  Offer as Form801ModalOffer,
  Listing as Form801ModalListing,
} from '../../admin/Form801Modal';
import ManageOfferNotificationModal, {
  ManageOfferNotificationModalListing,
} from '../../admin/ManageOfferNotificationModal';

type Offer = JsonObject<OfferDoc>;

export type OfferListTabs = 'registered' | 'inactive';
type FilterMap = Record<OfferListTabs, (offer: Offer) => boolean>;

interface TabData {
  text: string;
  value: OfferListTabs;
}

const TAB_DATA: TabData[] = [
  { text: 'Registered Offers', value: 'registered' },
  { text: 'Inactive Offers', value: 'inactive' },
];

const FILTER_MAP: FilterMap = {
  registered: offer => !offer.isDeleted,
  inactive: offer => offer.isDeleted,
};

export interface OfferListTableContainerProps {
  $rootScope: IRootScopeService & { auth: AuthContextUser };
  offerSettings?: OfferSettingsJsonType;
  listing: ListingWithReps;
  ModalService: any;
  showStatus?: boolean;
  showActions?: boolean;
  $routeParams: {
    showForm801Modal?: string;
    manageNotifications?: string;
    offerId?: string;
  };
  ToastService: any;
  $scope: any;
}

interface OfferListTableContainerState {
  activeTab: OfferListTabs;
  offers: Offer[];
  loading: boolean;
  offerSettings: OfferSettingsJsonType;
  showForm801Modal: boolean;
  showOfferNotificationModal: boolean;
  selectedOffer?: Form801ModalOffer;
  selectedListing?: ManageOfferNotificationModalListing | Form801ModalListing;
  offerActions?: ListingOfferNotificationsDTO;
}

class OfferListTableContainer extends React.Component<
  OfferListTableContainerProps,
  OfferListTableContainerState
> {
  constructor(props: OfferListTableContainerProps) {
    super(props);
    const { offerSettings } = props;
    this.state = {
      activeTab: 'registered',
      offers: [] as Offer[],
      offerSettings,
      loading: true,
      showForm801Modal: false,
      showOfferNotificationModal: false,
      selectedOffer: null,
      selectedListing: null,
      offerActions: null,
    };
  }

  public async componentDidMount() {
    await this.fetchData();

    // Handle opening modal from query params
    const { $routeParams, listing, ToastService, $scope } = this.props;
    const { offers } = this.state;
    if ($routeParams.showForm801Modal && $routeParams.offerId) {
      const foundOffer = offers.find(
        offer => offer.id === $routeParams.offerId,
      );
      if (foundOffer && !foundOffer.isVerified) {
        this.setState({
          showForm801Modal: true,
          selectedOffer: foundOffer,
          selectedListing: listing,
        });
      } else {
        $scope.$apply(
          ToastService.add({
            type: 'danger',
            message: 'The Offer Doc for this offer has already been managed',
          }),
        );
      }
    } else if ($routeParams.manageNotifications) {
      const pendingNotificationOffers = offers.find(
        offer => offer.isPendingNotification && !offer.isDeleted,
      );
      if (pendingNotificationOffers) {
        this.setState({
          showOfferNotificationModal: true,
          selectedListing: listing,
        });
      } else {
        $scope.$apply(
          ToastService.add({
            type: 'danger',
            message: 'Notifications for this offer have already been managed',
          }),
        );
      }
    }
  }

  public fetchData = async () => {
    const { offerSettings: existingOfferSettings } = this.state;
    const [offers, offerActions, offerSettings] = await Promise.all([
      this.fetchOffers(),
      this.fetchOfferActions(),
      !existingOfferSettings && this.fetchOfferSettings(),
    ]);
    this.setState({ offerSettings, offerActions, offers, loading: false });
  };

  public fetchOffers = async () => {
    const { listing } = this.props;
    const { data } = await Axios.get<Offer[]>(`/listing/${listing.id}/offer`);
    return data;
  };

  public fetchOfferSettings = async () => {
    const { listing } = this.props;
    const { data } = await Axios.get<OfferSettingsJsonType>(
      `/listing/${listing.id}/offer_settings`,
    );
    return data;
  };

  public fetchOfferActions = async () => {
    const { listing } = this.props;
    const { data } = await Axios.get<ListingOfferNotificationsDTO>(
      `/listing/${listing.id}/offer_actions`,
    );
    return data;
  };

  public setActiveTab = (tab: OfferListTabs) =>
    this.setState({ activeTab: tab });

  private offerPreview = (offerId: string) => {
    const { $rootScope, listing, ModalService } = this.props;
    $rootScope.$evalAsync(() => {
      ModalService.openOfferModal(listing.id, offerId, 'all');
    });
  };

  openForm801Modal = (
    listing: Partial<ListingWithReps>,
    offer: Form801ModalOffer,
  ) => {
    const listingWithPendingOffer = { ...listing, pendingOffers: [offer] };
    this.setState({
      showForm801Modal: true,
      selectedOffer: offer,
      selectedListing:
        listingWithPendingOffer as unknown as Form801ModalListing,
    });
  };

  closeForm801Modal = async (requireReload?: boolean, verified?: boolean) => {
    const { selectedListing } = this.state;
    this.setState(
      { showForm801Modal: false, selectedListing: null },
      async () => {
        if (requireReload) await this.fetchData();
        if (verified) this.openOfferNotificationModal(selectedListing);
      },
    );
  };

  openOfferNotificationModal = (
    listing: ManageOfferNotificationModalListing,
  ) => {
    this.setState({
      showOfferNotificationModal: true,
      selectedListing: listing,
    });
  };

  closeOfferNotificationModal = async (requireReload?: boolean) => {
    this.setState({ showOfferNotificationModal: false, selectedListing: null });
    if (requireReload) await this.fetchData();
  };

  public render() {
    const {
      activeTab,
      loading,
      offers,
      offerSettings,
      showForm801Modal,
      showOfferNotificationModal,
      selectedOffer,
      selectedListing,
      offerActions,
    } = this.state;
    const { listing, showActions, showStatus, $rootScope } = this.props;
    const isListingAgent =
      $rootScope?.auth?.id &&
      listing.reps.some(rep => rep.id === $rootScope.auth.id);
    const activeFilter = FILTER_MAP[activeTab];
    const filteredOffers = offers.filter(activeFilter);
    return (
      <>
        <ul className="nav nav-tabs active">
          {TAB_DATA.map(({ text, value }) => (
            <li
              key={value}
              className={value === activeTab ? 'active' : ''}
              role="presentation"
              onClick={() => this.setActiveTab(value)}
            >
              <a>
                {text}
                <span
                  style={{
                    fontWeight: 'bold',
                    color: 'red',
                    marginLeft: '0.5em',
                  }}
                >
                  {offers.filter(FILTER_MAP[value]).length}
                </span>
              </a>
            </li>
          ))}
        </ul>
        {loading ? (
          <Loading size="fa-4x" />
        ) : (
          <div className="offer-table-container">
            {filteredOffers.length === 0 ? (
              <Empty height={200} text={'No offers found for listing'} />
            ) : (
              <OfferListTable
                listing={listing}
                offers={filteredOffers}
                offerSettings={offerSettings}
                offerActions={offerActions}
                openOfferPreview={this.offerPreview}
                showStatus={showStatus}
                showActions={showActions}
                showRegistered={activeTab === 'registered'}
                openForm801Modal={this.openForm801Modal}
                openOfferNotificationModal={this.openOfferNotificationModal}
              />
            )}
          </div>
        )}

        {selectedOffer && selectedListing && (
          <Form801Modal
            offer={selectedOffer}
            show={showForm801Modal}
            listing={selectedListing as PendingRegisteredOffersListing}
            closeModal={this.closeForm801Modal}
          />
        )}

        {selectedListing && (
          <ManageOfferNotificationModal
            show={showOfferNotificationModal}
            listing={
              selectedListing as unknown as ManageOfferNotificationModalListing
            }
            closeModal={this.closeOfferNotificationModal}
            isListingAgent={isListingAgent}
          />
        )}
      </>
    );
  }
}

export default OfferListTableContainer;
